import type { ZohoOauthCredentials } from '@kunzite/shared-types';
import React, { useEffect, useRef, useState } from 'react';

import { exchangeAuth, AuthExchangeError, ZohoTokenHolder } from './boot/auth-exchange';
import { loadZohoDeskSdk } from './boot/zoho-sdk';
import { ensureMicPermission, MicPermissionError } from './call/mic-permission';
import { MultiLegOrchestrator, type MultiLegState } from './call/multi-leg-orchestrator';
import { createRingtone } from './call/ringtone';
import { CallSessionOrchestrator } from './call/session';
import { AuditClient } from './services/audit-client';
import { CallsApiClient } from './services/calls-api';
import { useWidgetServices, WidgetServicesProvider, type WidgetServices } from './services/services-context';
import { TenantsApiClient } from './services/tenants-api';
import type { WrappedSession } from './sip/ua';
import { startUserAgent } from './sip/ua';
import { useWidgetStore, type WidgetState } from './state/store';
import { ActiveCall } from './ui/active-call';
import { CipherCorruptModal } from './ui/cipher-corrupt-modal';
import { Dialer } from './ui/dialer';
import { IncomingCallModal } from './ui/incoming-call';
import { OAuthRevokedModal } from './ui/oauth-revoked-modal';
import { PresenceToggle } from './ui/presence';
import { StatusView } from './ui/status';
import { TransferPanel, type TransferPanelMode } from './ui/transfer-panel';
import { WrapUpPanel } from './ui/wrap-up-panel';
import { createWsDispatcher } from './ws/dispatcher';
import { WidgetEventsClient } from './ws/widget-events';
import { DeskInvokeClient } from './zoho/desk-invoke';
import { PhoneBridgeClient } from './zoho/phonebridge-client';
import { popContact } from './zoho/screen-pop';

const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'https://api-zohodesk.kunzite.app';
const WS_URL =
  (import.meta.env.VITE_WS_URL as string | undefined) ??
  'wss://api-zohodesk.kunzite.app/v1/widget-events';

export function App(): React.JSX.Element {
  const state = useWidgetStore();
  const bootedRef = useRef(false);
  const [services, setServices] = useState<WidgetServices | null>(null);

  useEffect(() => {
    if (state.status !== 'booting' || bootedRef.current) return;
    bootedRef.current = true;

    let stopUa = (): void => undefined;
    let wsClient: WidgetEventsClient | null = null;
    const ringtone = createRingtone();

    void (async () => {
      try {
        const App = await loadZohoDeskSdk();
        const user = App.instance.user;
        const org = App.instance.organization;
        const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));

        useWidgetStore.getState().setAuthenticating();
        const exchange = await exchangeAuth(
          { baseUrl: API_BASE_URL },
          {
            zohoOrgId: org.id,
            zohoUserId: user.id,
            zohoUserEmail: user.emailId,
            installSignature: params.get('sig') ?? '',
            signedAt: params.get('signedAt') ?? new Date().toISOString(),
          },
        );

        await ensureMicPermission();

        const session = {
          jwt: exchange.jwt,
          sipExtension: exchange.agent.sipExtension,
          sipDomain: exchange.sip.domain,
          sipWssUrl: exchange.sip.wssUrl,
          sipPassword: exchange.sip.password,
        };
        useWidgetStore.getState().setRegistering(session);

        const zohoToken = new ZohoTokenHolder();
        zohoToken.setInitial(exchange.zohoOauth, async () => {
          const fresh = await exchangeAuth(
            { baseUrl: API_BASE_URL },
            {
              zohoOrgId: org.id,
              zohoUserId: user.id,
              zohoUserEmail: user.emailId,
              installSignature: params.get('sig') ?? '',
              signedAt: params.get('signedAt') ?? new Date().toISOString(),
            },
          );
          return fresh.zohoOauth;
        });

        const onJwtUpdate = (newJwt: string, fresh: { expiresAt: string; refreshExpiresAt: string; zohoOauth: ZohoOauthCredentials }): void => {
          // F4.1-A: shared closure invoked by refreshOn401 from any of the three
          // widget API clients on /v1/auth/refresh success. Updates the captured
          // exchange reference + the ZohoTokenHolder so subsequent calls see
          // fresh credentials.
          zohoToken.updateCreds(fresh.zohoOauth);
          Object.assign(exchange, { jwt: newJwt, expiresAt: fresh.expiresAt });
        };

        const phonebridge = new PhoneBridgeClient({
          baseUrl: API_BASE_URL,
          tokenProvider: () => Promise.resolve({ jwt: exchange.jwt }),
          onJwtUpdate,
        });
        const desk = new DeskInvokeClient({ isBaseUrl: API_BASE_URL, jwt: exchange.jwt });
        const calls = new CallsApiClient({
          baseUrl: API_BASE_URL,
          tokenProvider: () => Promise.resolve({ jwt: exchange.jwt }),
          onJwtUpdate,
        });
        const tenants = new TenantsApiClient({
          baseUrl: API_BASE_URL,
          tokenProvider: () => Promise.resolve({ jwt: exchange.jwt }),
          onJwtUpdate,
        });
        const auditClient = new AuditClient({
          baseUrl: API_BASE_URL,
          tokenProvider: () => Promise.resolve({ jwt: exchange.jwt }),
          onJwtUpdate,
        });
        let placeCallFn = (_to: string): WrappedSession | undefined => undefined;

        const multiLeg = new MultiLegOrchestrator({
          placeCall: (target) => {
            // Reuses the UA's placeCall via the placeCallFn hoisted further down.
            // The ua.placeCall returns a WrappedSession (per ua.ts).
            const result = placeCallFn(target);
            if (!result) {
              throw new Error('multiLeg.placeCall: UA placeCall returned undefined');
            }
            return result;
          },
          onStateChange: (s: MultiLegState) => {
            // Sync to widget store so TransferPanel mode reflects orchestrator state.
            if (s.kind === 'consulting') {
              useWidgetStore.getState().setConsult(s.consult, s.consultTarget);
            } else if (s.kind === 'transferring') {
              useWidgetStore.getState().setTransferring();
            } else if (s.kind === 'conferencing') {
              // F4.5.1: propagate conferencing state to the store so
              // TransferPanel renders conference-active mode. The previous
              // session.ts:381 comment claimed this was already happening,
              // but the branch was missing — discovered live during F4.4
              // acceptance and the misleading "Completar transferencia"
              // button stayed visible.
              useWidgetStore.getState().setConferencing();
            } else if (s.kind === 'primary-active' || s.kind === 'idle') {
              useWidgetStore.getState().clearConsult();
            }
          },
          // F4.2-acceptance HF4: omit referReplaces injection — the orchestrator
          // falls back to primary.referReplaces(consult) on the WrappedSession,
          // which uses JsSIP's native replaces API and handles dialog-tag
          // extraction internally (no more `as unknown as DialogIdentifiers` cast).
        });

        const orch = new CallSessionOrchestrator({
          phonebridge,
          store: useWidgetStore.getState(),
          sipExtension: session.sipExtension,
          sipDomain: session.sipDomain,
          zohouser: user.id,
          callsApi: calls,
          multiLeg,
          auditClient,
        });
        const ua = startUserAgent(
          {
            jwt: session.jwt,
            sipExtension: session.sipExtension,
            wssUrl: session.sipWssUrl,
            domain: session.sipDomain,
            ...(session.sipPassword ? { password: session.sipPassword } : {}),
          },
          {
            onRegistered: () => {
              useWidgetStore.getState().setReady();
              useWidgetStore.getState().setPresence('online');
            },
            onRegistrationFailed: (cause) =>
              useWidgetStore
                .getState()
                .setError({ code: 'sip_register_failed', message: cause }),
            onDisconnected: () =>
              useWidgetStore
                .getState()
                .setError({ code: 'sip_disconnected', message: 'WSS disconnected' }),
            onIncomingCall: ({ callId, from, session: sipSession }) => {
              ringtone.start();
              void (async () => {
                await orch.onIncoming({ callId, from, session: sipSession });
                try {
                  const result = await desk.searchContactByPhone({
                    orgId: org.id,
                    phoneE164: from,
                    deskApiBaseUrl: 'https://desk.zoho.com',
                  });
                  const data = JSON.parse(result.response) as {
                    data?: Array<{ id: string }>;
                  };
                  const first = data.data?.[0];
                  if (data.data && data.data.length === 1 && first) popContact(first.id);
                } catch {
                  /* tolerated; no screen-pop */
                }
              })();
            },
            onOutgoingCallProgress: ({ callId, to, session: sipSession }) => {
              void orch.onOutgoingProgress({ callId, to, session: sipSession });
            },
            onCallAnswered: ({ callId }) => {
              ringtone.stop();
              void orch.onAnswered({ callId });
            },
            onCallEnded: ({ callId, cause }) => {
              ringtone.stop();
              void orch.onEnded({ callId, cause });
            },
            onCallFailed: ({ callId, cause }) => {
              ringtone.stop();
              void orch.onEnded({ callId, cause });
            },
          },
        );
        stopUa = ua.stop;
        placeCallFn = (to) => ua.placeCall(to);

        const dispatch = createWsDispatcher({ orch, ua: { placeCall: (t) => ua.placeCall(t) } });
        wsClient = new WidgetEventsClient({ url: WS_URL, jwt: exchange.jwt, onEvent: dispatch });
        wsClient.connect();

        setServices({
          orch,
          phonebridge,
          desk,
          calls,
          tenants,
          zohoToken,
          auditClient,
          placeCall: placeCallFn,
          saveWrapUp: async (callId, input) => {
            const r = await calls.saveWrapUp(callId, input);
            return { ok: true, pending: r.pending };
          },
        });
      } catch (err) {
        if (err instanceof MicPermissionError) {
          useWidgetStore.getState().setError({ code: err.code, message: err.message });
        } else if (err instanceof AuthExchangeError) {
          useWidgetStore.getState().setError({ code: err.code, message: err.message });
        } else {
          useWidgetStore
            .getState()
            .setError({
              code: 'boot_failed',
              message: err instanceof Error ? err.message : String(err),
            });
        }
        bootedRef.current = false;
      }
    })();

    return () => {
      stopUa();
      wsClient?.disconnect();
    };
  }, [state.status]);

  // Invariant: services === null until boot completes (SIP UA + WS wired).
  // While null, state.status is booting / authenticating / registering / error,
  // and StatusView is the only meaningful UI. The store's setReady() runs only
  // AFTER setServices(...) inside the boot block, so status === 'ready' implies
  // services !== null on every reachable code path.
  if (services === null) {
    return (
      <>
        <main style={{ padding: 16 }}>
          <h1>Kunzite</h1>
          <StatusView state={state} />
        </main>
        <OAuthRevokedModal />
        <CipherCorruptModal />
      </>
    );
  }

  return (
    <WidgetServicesProvider services={services}>
      <ChromeShell state={state} />
      <OAuthRevokedModal />
      <CipherCorruptModal />
    </WidgetServicesProvider>
  );
}

function ChromeShell({ state }: { state: WidgetState }): React.JSX.Element {
  const services = useWidgetServices();
  const inCall = state.call !== null && state.call.state !== 'ended';
  const [transferPanelOpen, setTransferPanelOpen] = useState(false);
  const transferState = useWidgetStore((s) => s.transferState);
  const transferTarget = useWidgetStore((s) => s.transferTarget);

  const transferMode: TransferPanelMode =
    transferState === 'consulting' ? 'consult-active'
    : transferState === 'transferring' ? 'transferring'
    : transferState === 'conferencing' ? 'conference-active'
    : 'blind';
  const handleDial = (number: string): void => services.placeCall(number);
  const handleAccept = (): void => void services.orch.acceptIncoming();
  const handleReject = (): void => void services.orch.rejectIncoming();
  const handleHangup = (): void => services.orch.hangup();
  const handleMute = (m: boolean): void => services.orch.setMuted(m);
  const handleHold = (h: boolean): void => services.orch.setHold(h);
  const handleDtmf = (d: string): void => services.orch.sendDtmf(d);

  return (
    <main style={{ padding: 16 }}>
      <h1>Kunzite</h1>
      <StatusView state={state} />
      {state.status === 'ready' && (
        <>
          <PresenceToggle
            presence={state.presence}
            onChange={(p) => useWidgetStore.getState().setPresence(p)}
            disabled={inCall}
          />
          {!inCall && !state.wrapUp.open && (
            <Dialer onDial={handleDial} disabled={state.presence !== 'online'} />
          )}
          {state.call?.state === 'ringing' &&
            state.call.direction === 'inbound' &&
            !state.wrapUp.open && (
              <IncomingCallModal
                remote={state.call.remote}
                onAccept={handleAccept}
                onReject={handleReject}
              />
            )}
          {state.call &&
            (state.call.state === 'answered' || state.call.state === 'on-hold') &&
            !state.wrapUp.open && (
              <ActiveCall
                remote={state.call.remote}
                muted={state.call.muted}
                held={state.call.state === 'on-hold'}
                answeredAt={state.call.answeredAt ?? Date.now()}
                onMute={handleMute}
                onHold={handleHold}
                onHangup={handleHangup}
                onDtmf={handleDtmf}
                onOpenTransfer={() => setTransferPanelOpen(true)}
              />
            )}
          {transferPanelOpen && (
            <TransferPanel
              mode={transferMode}
              defaultCountry="ES"
              {...(transferTarget != null ? { consultTarget: transferTarget } : {})}
              {...(state.call?.remote != null ? { primaryRemote: state.call.remote } : {})}
              onBlindTransfer={(target) => {
                void (async () => {
                  try { await services.orch.blindTransfer(target); }
                  catch (err) { console.error('[transfer] blind failed', err); }
                  finally { setTransferPanelOpen(false); }
                })();
              }}
              onStartConsult={(target) => {
                try { services.orch.startConsult(target); }
                catch (err) {
                  console.error('[transfer] start consult failed', err);
                  setTransferPanelOpen(false);
                }
              }}
              onCompleteTransfer={() => {
                void (async () => {
                  try { await services.orch.completeWarmTransfer(); }
                  catch (err) { console.error('[transfer] complete failed', err); }
                  finally { setTransferPanelOpen(false); }
                })();
              }}
              onCancelConsult={() => {
                void (async () => {
                  try { await services.orch.cancelConsult(); }
                  catch (err) { console.error('[transfer] cancel consult failed', err); }
                })();
              }}
              onAddToConference={() => {
                void (async () => {
                  try { await services.orch.addToConference(); }
                  catch (err) { console.error('[transfer] addToConference failed', err); }
                  // keep modal open in conference-active state
                })();
              }}
              onEndConference={() => {
                void (async () => {
                  try { await services.orch.endConference(); }
                  catch (err) { console.error('[transfer] endConference failed', err); }
                  finally { setTransferPanelOpen(false); }
                })();
              }}
              onDropPrimary={() => {
                void (async () => {
                  try { await services.orch.dropConferenceParticipant('primary'); }
                  catch (err) { console.error('[transfer] dropConferenceParticipant primary failed', err); }
                  finally { setTransferPanelOpen(false); }
                })();
              }}
              onDropConsult={() => {
                void (async () => {
                  try { await services.orch.dropConferenceParticipant('consult'); }
                  catch (err) { console.error('[transfer] dropConferenceParticipant consult failed', err); }
                  finally { setTransferPanelOpen(false); }
                })();
              }}
              onCancel={() => setTransferPanelOpen(false)}
            />
          )}
          {state.wrapUp.open && <WrapUpPanel />}
        </>
      )}
    </main>
  );
}
